import { useMemo, useEffect } from 'react'
import { fetchCruiseOfferSnapshot } from 'actions/CruiseActions'
import { fetchOrderItemCruiseDetails } from 'actions/OrderActions'
import { useAppDispatch, useAppSelector, shallowEqual } from 'hooks/reduxHooks'
import { canAddInsuranceToOrder } from 'lib/order/orderUtils'
import { getCabinCategories } from 'lib/cruises/cruiseUtils'

interface CruiseState {
  offers?: { [key: string]: App.CruiseOffer }
  bookings?: { [key: string]: App.CruiseBookingInfo }
  departures?: { [key: string]: App.CruiseDepartureView }
  canAddInsurance?: boolean
  insuranceProductName?: string
  offerSnapshot?: App.Cruises.CruiseOfferSnapshot
  booking?: App.Cruises.CruiseBooking
}

export default function useCruisePurchaseDetails(
  order: App.Order,
  bookingId: string,
) {
  const dispatch = useAppDispatch()
  const cruiseItem = order.cruiseItems[0]
  const offerId = cruiseItem?.cruiseOfferId
  const offer = useAppSelector<App.CruiseOffer | undefined>((state) => (
    offerId ? state.cruise.cruiseOffers[offerId] : undefined
  ), shallowEqual)

  const {
    offers,
    insuranceProductName,
    canAddInsurance,
    offerSnapshot,
    booking,
  } = useAppSelector<CruiseState>((state) => ({
    offers: state.cruise.cruiseOffers,
    insuranceProductName: state.geo.insuranceProductName,
    canAddInsurance: canAddInsuranceToOrder(order, state.geo.currentRegionCode, offer),
    offerSnapshot: state.cruise.cruiseOfferSnapshot[bookingId],
    booking: cruiseItem ? state.orders.cruiseDetails[cruiseItem.id]?.cruise : undefined,
  }), shallowEqual)

  const offerInfo = useMemo(() => {
    if (offers && offerId) return offers[offerId]
  }, [offerId, offers])

  const travelers = useMemo(() => {
    const travelers = booking?.travellersDetails!
    if (travelers?.length > 0) {
      const numberOfAdults = travelers.filter((item) => item.type === 'adult').length
      const numberOfChilds = travelers.filter((item) => item.type === 'child').length
      return { numberOfAdults, numberOfChilds }
    }
  }, [booking?.travellersDetails])

  const cabinInfo = useMemo(() => {
    if ((offerInfo?.ship.cabinCategories?.length! > 0) &&
      (booking?.cabinCode || booking?.categoryId)) {
      return offerInfo?.ship.cabinCategories?.find(
        (item) => item.code === (booking.cabinCode || booking.categoryId))
    }
  }, [offerInfo?.ship.cabinCategories, booking?.cabinCode, booking?.categoryId])

  const image = useMemo(() => {
    if (offerSnapshot) {
      return (
        offerSnapshot.heroImageId ??
        offerSnapshot.shipImageId ??
        offerSnapshot.cabinImageId
      )
    }

    if (offerInfo?.ship?.images?.length) {
      return offerInfo.ship.images[0].id
    }

    if (booking?.cabinType) {
      const cabinCategoriesBasedOnCruiseType = getCabinCategories(offerInfo?.cruiseType)

      const cabinCategory = cabinCategoriesBasedOnCruiseType.find(
        (category) => category.category === booking.cabinType,
      )
      return cabinCategory?.defaultImageId
    }
  }, [booking?.cabinType, offerInfo?.ship?.images, offerSnapshot, offerInfo?.cruiseType])

  useEffect(() => {
    if (bookingId) {
      dispatch(fetchCruiseOfferSnapshot(bookingId))
    }
  }, [bookingId, dispatch])

  useEffect(() => {
    if (cruiseItem?.id) {
      dispatch(fetchOrderItemCruiseDetails(order.id, cruiseItem.id))
    }
  }, [cruiseItem?.id, dispatch, order.id])

  return {
    image,
    name: offerSnapshot?.offerName,
    bookingNumber: booking?.confirmationNumber,
    cruiseItem,
    offerInfo,
    travelers,
    cabinInfo,
    insuranceProductName,
    canAddInsurance,
    offerSnapshot,
    booking,
  }
}
